import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { updateUserPassword } from '../api/authApi';
import { Toast } from '../components/ui';
import { getUser } from '../utils/auth';
import styles from './Account.module.scss';

const validatePassword = password =>
  !password || password.length < 6 ? 'Password must have at least 6 characters.' : '';

function Account() {
  const user = getUser();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async event => {
    event.preventDefault();
    setFormError('');
    const validationError = validatePassword(form.next);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (form.next !== form.confirm) {
      setFormError('New passwords do not match.');
      return;
    }
    try {
      setSaving(true);
      await updateUserPassword(user.username, {
        current_password: form.current,
        new_password: form.next,
      });
      setForm({ current: '', next: '', confirm: '' });
      setSuccess('Password updated.');
    } catch (requestError) {
      setFormError(requestError.response?.data?.detail || 'Failed to update password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Account</span>
          <h1>My account</h1>
          <p>Review your access details and keep your password up to date.</p>
        </div>
      </header>

      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}

      <section className={styles.panel}>
        <div className={styles.identity}>
          <div className={styles.avatar} aria-hidden="true">
            {(user?.username || 'U').slice(0, 1).toUpperCase()}
          </div>
          <div>
            <strong>{user?.username}</strong>
            <span className={styles.roleLine}>{user?.role}</span>
          </div>
        </div>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <KeyRound size={17} />
          <div>
            <h2>Change password</h2>
            <p>Use at least 6 characters.</p>
          </div>
        </div>
        <form className={styles.form} onSubmit={handleSubmit}>
          {formError && <div className={styles.formError}>{formError}</div>}
          <label>
            <span>Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={form.current}
              onChange={event => setForm(current => ({ ...current, current: event.target.value }))}
            />
          </label>
          <label>
            <span>New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={form.next}
              onChange={event => setForm(current => ({ ...current, next: event.target.value }))}
            />
          </label>
          <label>
            <span>Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={event => setForm(current => ({ ...current, confirm: event.target.value }))}
            />
          </label>
          <div className={styles.formActions}>
            <button type="submit" className={styles.primaryButton} disabled={saving}>
              {saving ? 'Saving…' : 'Update password'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default Account;
